import React, { useMemo, useState } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Card,
  CardContent,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  CheckCircle as InstalledIcon,
  Error as ErrorIcon,
  Downloading as DownloadingIcon,
  HourglassTop as PendingIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useAllUpdates } from '../hooks/useAllUpdates';
import { useDevices } from '../hooks/useDevices';
import { isUpdateStalled } from '../utils/updates';
import { DeviceUpdate } from '../types';

type StatusFilter = 'all' | DeviceUpdate['status'];

const statusMeta: Record<DeviceUpdate['status'], { label: string; color: 'success' | 'error' | 'warning' | 'info'; icon: React.ReactNode }> = {
  pending: { label: 'Pendente', color: 'warning', icon: <PendingIcon /> },
  downloading: { label: 'Baixando', color: 'info', icon: <DownloadingIcon /> },
  installing: { label: 'Instalando', color: 'info', icon: <DownloadingIcon /> },
  installed: { label: 'Instalado', color: 'success', icon: <InstalledIcon /> },
  failed: { label: 'Falhou', color: 'error', icon: <ErrorIcon /> },
};

const UpdatesLog: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { updates, loading, error } = useAllUpdates(!!user);
  const { devices } = useDevices(!!user);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mapa id → estado de conexão
  const deviceOnlineById = useMemo(() => {
    const map = new Map<string, boolean>();
    devices.forEach(d => map.set(d.id, d.online));
    return map;
  }, [devices]);

  // Mapa id → nome de dispositivo
  const deviceNameById = useMemo(() => {
    const map = new Map<string, string>();
    devices.forEach(d => {
      map.set(d.id, d.customName || d.name || d.id);
    });
    return map;
  }, [devices]);

  const filteredUpdates = useMemo(() => {
    return updates.filter(u => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (deviceFilter !== 'all' && u.deviceId !== deviceFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const deviceName = (deviceNameById.get(u.deviceId) || '').toLowerCase();
        if (!u.id.toLowerCase().includes(term) &&
            !u.deviceId.toLowerCase().includes(term) &&
            !deviceName.includes(term) &&
            !(u.errorMessage || '').toLowerCase().includes(term) &&
            !(u.apkUrl || '').toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [updates, statusFilter, deviceFilter, searchTerm, deviceNameById]);

  const stats = useMemo(() => {
    return {
      total: updates.length,
      installed: updates.filter(u => u.status === 'installed').length,
      failed: updates.filter(u => u.status === 'failed').length,
      pending: updates.filter(u => u.status === 'pending').length,
      inProgress: updates.filter(u => u.status === 'downloading' || u.status === 'installing').length,
    };
  }, [updates]);

  const clearFilters = () => {
    setStatusFilter('all');
    setDeviceFilter('all');
    setSearchTerm('');
  };

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <AppBar position="static" color="default" elevation={1} sx={{ bgcolor: '#fff' }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Logs de Atualização
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
        {/* Estatísticas */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Paper sx={{ flex: 1, p: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h5">{stats.total}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderLeft: 4, borderColor: 'success.main' }}>
            <Typography variant="caption" color="text.secondary">Instalados</Typography>
            <Typography variant="h5" color="success.main">{stats.installed}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderLeft: 4, borderColor: 'info.main' }}>
            <Typography variant="caption" color="text.secondary">Em andamento</Typography>
            <Typography variant="h5" color="info.main">{stats.inProgress}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderLeft: 4, borderColor: 'warning.main' }}>
            <Typography variant="caption" color="text.secondary">Pendentes</Typography>
            <Typography variant="h5" color="warning.main">{stats.pending}</Typography>
          </Paper>
          <Paper sx={{ flex: 1, p: 2, textAlign: 'center', borderLeft: 4, borderColor: 'error.main' }}>
            <Typography variant="caption" color="text.secondary">Falhas</Typography>
            <Typography variant="h5" color="error.main">{stats.failed}</Typography>
          </Paper>
        </Stack>

        {/* Filtros */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por ID, dispositivo, erro ou APK..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="pending">Pendente</MenuItem>
                  <MenuItem value="downloading">Baixando</MenuItem>
                  <MenuItem value="installing">Instalando</MenuItem>
                  <MenuItem value="installed">Instalado</MenuItem>
                  <MenuItem value="failed">Falhou</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Dispositivo</InputLabel>
                <Select
                  value={deviceFilter}
                  label="Dispositivo"
                  onChange={(e) => setDeviceFilter(e.target.value)}
                >
                  <MenuItem value="all">Todos</MenuItem>
                  {devices.map(d => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.customName || d.name || d.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                disabled={statusFilter === 'all' && deviceFilter === 'all' && !searchTerm}
              >
                Limpar
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Lista de updates */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : filteredUpdates.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {updates.length === 0 ? 'Nenhuma atualização registrada' : 'Nenhum resultado para os filtros selecionados'}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Exibindo {filteredUpdates.length} de {updates.length} atualizações
                </Typography>
                <List disablePadding>
                  {filteredUpdates.map((update, index) => {
                    const meta = statusMeta[update.status];
                    const deviceName = deviceNameById.get(update.deviceId) || update.deviceId;
                    const apkName = update.apkUrl
                      ? (update.apkUrl.split('/').pop()?.split('?')[0]?.split('_').pop() || 'APK desconhecido')
                      : 'APK desconhecido';

                    return (
                      <React.Fragment key={update.id}>
                        {index > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                          <ListItemIcon sx={{ mt: 1 }}>
                            <Avatar sx={{ bgcolor: `${meta.color}.main` }}>
                              {meta.icon}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {deviceName}
                                </Typography>
                                <Chip label={meta.label} color={meta.color} size="small" />
                                {update.updateType === 'newInstall' && (
                                  <Chip label="Nova Instalação" variant="outlined" size="small" />
                                )}
                              </Stack>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" component="div">
                                  Criado em: {format(update.createdAt, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                                  {' '}
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    ({formatDistanceToNow(update.createdAt, { addSuffix: true, locale: ptBR })})
                                  </Typography>
                                </Typography>
                                {update.updatedAt !== update.createdAt && (
                                  <Typography variant="caption" color="text.secondary" component="div">
                                    Última alteração: {format(update.updatedAt, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                                  </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" component="div">
                                  Update ID: {update.id} · Device ID: {update.deviceId}
                                </Typography>
                                <Tooltip title={update.apkUrl || ''} arrow>
                                  <Typography variant="caption" color="text.secondary" component="div" sx={{ cursor: 'pointer' }}>
                                    APK: {apkName}
                                  </Typography>
                                </Tooltip>
                                {isUpdateStalled(update, !!deviceOnlineById.get(update.deviceId)) && (
                                  <Alert severity="warning" sx={{ mt: 1 }}>
                                    Online, mas sem sinal de ter recebido esta atualização —
                                    canal de atualização provavelmente travado. Reinicie o
                                    launcher deste dispositivo.
                                  </Alert>
                                )}
                                {update.status === 'failed' && update.errorMessage && (
                                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                      Erro reportado pelo dispositivo:
                                    </Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                      {update.errorMessage}
                                    </Typography>
                                    {update.errorTimestamp && (
                                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
                                        {format(update.errorTimestamp, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default UpdatesLog;
